import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConfigurationGuide = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      title: "Crear Aplicación en MercadoLibre",
      description: "Registra tu aplicación en el portal de desarrolladores",
      icon: "Plus",
      content: [
        "Visita https://developers.mercadolibre.com",
        "Inicia sesión con tu cuenta de MercadoLibre",
        "Ve a \'Mis Aplicaciones\' y haz clic en \'Crear aplicación'",
        "Completa el formulario con los datos de tu aplicación",
        "Guarda el Client ID y Client Secret generados"
      ]
    },
    {
      title: "Configurar Credenciales",
      description: "Ingresa tus credenciales de API en el formulario",
      icon: "Key",
      content: [
        "Copia el Client ID desde el portal de desarrolladores",
        "Copia el Client Secret (manténlo seguro)",
        "Selecciona el mercado donde vas a operar",
        "Haz clic en \'Guardar Credenciales'",
        "Verifica que no haya errores en la configuración"
      ]
    },
    {
      title: "Autorizar Aplicación",
      description: "Completa el flujo OAuth para obtener permisos",
      icon: "Shield",
      content: [
        "Haz clic en 'Conectar con MercadoLibre'",
        "Se abrirá una ventana de autorización oficial",
        "Inicia sesión con tu cuenta de vendedor",
        "Acepta los permisos solicitados",
        "La ventana se cerrará automáticamente al completar"
      ]
    },
    {
      title: "Configurar Webhooks (Opcional)",
      description: "Integra con n8n para automatización avanzada",
      icon: "Webhook",
      content: [
        "Configura tu instancia de n8n",
        "Crea un webhook endpoint para recibir eventos",
        "Ingresa la URL del webhook en la configuración",
        "Genera o ingresa un token de autenticación",
        "Prueba la conexión con el botón \'Probar Webhook'"
      ]
    }
  ].filter((step) => step.icon !== 'Webhook');

  const nextStep = () => {
    if (activeStep < steps?.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="BookOpen" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="font-semibold text-text-primary">Guía de Configuración</h3>
          <p className="text-sm text-text-secondary">Sigue estos pasos para configurar tu integración</p>
        </div>
      </div>
      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-8">
        {steps?.map((step, index) => (
          <div key={index} className="flex items-center">
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-smooth cursor-pointer ${
                index === activeStep 
                  ? 'bg-primary text-primary-foreground' 
                  : index < activeStep 
                    ? 'bg-success text-success-foreground'
                    : 'bg-muted text-text-secondary'
              }`}
              onClick={() => setActiveStep(index)}
            >
              {index < activeStep ? (
                <Icon name="Check" size={16} />
              ) : (
                index + 1
              )}
            </div>
            {index < steps?.length - 1 && (
              <div className={`w-12 h-0.5 mx-2 ${index < activeStep ? 'bg-success' : 'bg-border'}`}></div>
            )}
          </div>
        ))}
      </div>
      {/* Current Step Content */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-primary/10`}>
            <Icon name={steps?.[activeStep]?.icon} size={24} className="text-primary" />
          </div>
          <div>
            <h4 className="font-semibold text-text-primary">{steps?.[activeStep]?.title}</h4>
            <p className="text-sm text-text-secondary">{steps?.[activeStep]?.description}</p>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <ol className="list-decimal list-inside space-y-2">
            {steps?.[activeStep]?.content?.map((item, index) => (
              <li key={index} className="text-sm text-text-primary">{item}</li>
            ))}
          </ol>
        </div>
      </div>
      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={activeStep === 0}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Anterior
        </Button>

        <div className="text-sm text-text-secondary">
          Paso {activeStep + 1} de {steps?.length}
        </div>

        <Button
          variant="outline"
          onClick={nextStep}
          disabled={activeStep === steps?.length - 1}
          iconName="ChevronRight"
          iconPosition="right"
        >
          Siguiente
        </Button>
      </div>
      {/* Quick Links */}
      <div className="mt-6 pt-6 border-t border-border">
        <h5 className="font-medium text-text-primary mb-3">Enlaces Útiles</h5>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href="https://developers.mercadolibre.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-smooth"
          >
            <Icon name="ExternalLink" size={16} className="text-primary" />
            <span className="text-sm font-medium text-text-primary">Portal de Desarrolladores</span>
          </a>
          <a
            href="https://developers.mercadolibre.com/es_ar/api-docs-es"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-smooth"
          >
            <Icon name="FileText" size={16} className="text-primary" />
            <span className="text-sm font-medium text-text-primary">Documentación API</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationGuide;